import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { apiClient } from '@/services/api';
import { useProfileStore } from '@/store/profile.store';
import { profileService } from '@/services/profile.service';
import { FullLoader } from '@shared/ui/loading';

export interface PrivateRouteProps {
  children: ReactNode;
  mode?: 'cloud' | 'local';
  skipProfileCheck?: boolean;
}

export function PrivateRoute({ children, mode = 'cloud', skipProfileCheck = false }: PrivateRouteProps) {
  const { isAuthenticated, isLoading, hasHydrated, checkAuth } = useAuth();
  const location = useLocation();
  const [profileChecked, setProfileChecked] = useState(false);
  const [hasProfile, setHasProfile] = useState(true); // default true to avoid flash redirect

  const requiresAuth = mode === 'cloud';
  const isOnboardingPage = location.pathname === '/app/onboarding';

  useEffect(() => {
    if (!hasHydrated || !requiresAuth) return;
    if (!isAuthenticated && !isLoading) {
      checkAuth();
    }
  }, [isAuthenticated, isLoading, hasHydrated, checkAuth, requiresAuth]);

  // Check if user has a profile (only once per session for authenticated users)
  // If no server profile but local profile exists, auto-sync it to server
  useEffect(() => {
    if (!requiresAuth || !isAuthenticated || isOnboardingPage || skipProfileCheck || profileChecked) return;

    const checkProfile = async () => {
      try {
        const response = await apiClient.get('/api/v1/profile/exists');
        const exists = response.data?.data?.exists ?? true;

        if (!exists) {
          // No server profile — try to sync local profile to server
          const localProfile = useProfileStore.getState().profile;
          if (localProfile) {
            console.log('[PrivateRoute] No server profile, syncing local profile...');
            try {
              await profileService.syncToServer(localProfile);
              console.log('[PrivateRoute] Local profile synced to server');
              setHasProfile(true);
              return;
            } catch (syncErr) {
              console.warn('[PrivateRoute] Failed to sync local profile:', syncErr);
            }
          }
        }

        setHasProfile(exists);
      } catch {
        // On error, don't block — assume profile exists
        setHasProfile(true);
      } finally {
        setProfileChecked(true);
      }
    };

    checkProfile();
  }, [isAuthenticated, requiresAuth, isOnboardingPage, skipProfileCheck, profileChecked]);

  if (!hasHydrated || (requiresAuth && isLoading)) {
    return <FullLoader message="Checking authentication..." />;
  }

  if (requiresAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if authenticated but no profile (and not already on onboarding)
  if (requiresAuth && isAuthenticated && profileChecked && !hasProfile && !isOnboardingPage) {
    return <Navigate to="/app/onboarding" replace />;
  }

  // Wait for profile check before rendering (except on onboarding page)
  if (requiresAuth && isAuthenticated && !profileChecked && !isOnboardingPage && !skipProfileCheck) {
    return <FullLoader message="Loading your profile..." />;
  }

  return <>{children}</>;
}
